import type {
  Country,
  Currency,
  PlanInfo,
  PricingDatum,
  BillingCycle
} from '@/types/apply'

// Shared WebMCP (Web Model Context Protocol) plumbing.
//
// WebMCP lets a public page advertise structured, callable tools to an AI agent
// operating in the same browser, via the W3C `navigator.modelContext` API
// (https://github.com/webmachinelearning/webmcp). As of 2026 it ships behind a
// Chrome origin trial, so EVERYTHING here is feature-detected: when the API is
// absent (the common case today) the helpers below are no-ops and the normal
// human experience is untouched.
//
// This module holds the pieces shared by more than one surface:
//   - the tiny type surface for tools + `navigator.modelContext`
//   - argument-coercion helpers (`asString`, `asStringArray`, `group`)
//   - the two READ-ONLY tools (`wavbee_list_plans_and_pricing`,
//     `wavbee_check_country_supported`) which read public info only
//   - `registerWebMcpTools`, the feature-detected register + cleanup routine
//
// The apply page (`useWebMcp`) builds on these and adds its own write-ish
// prefill tool; the homepage (`useHomeWebMcp`) registers only the read-only
// pair. Any surface registering tools must be the ONLY one mounted at a time —
// `provideContext` replaces the entire toolset (see `registerWebMcpTools`). A
// visitor is only ever on one route, so that holds.

export interface WebMcpToolResult {
  content: Array<{ type: 'text'; text: string }>
}

export interface WebMcpTool {
  name: string
  description: string
  inputSchema?: Record<string, unknown>
  execute: (args: Record<string, unknown>) => WebMcpToolResult | Promise<WebMcpToolResult>
}

interface ModelContext {
  provideContext?: (ctx: { tools: WebMcpTool[] }) => void
  registerTool?: (tool: WebMcpTool) => void
  unregisterTool?: (name: string) => void
}

type NavigatorWithModelContext = Navigator & { modelContext?: ModelContext }

export function text(t: string): WebMcpToolResult {
  return { content: [{ type: 'text', text: t }] }
}

export function asString(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim() !== '') return v.trim()
  return undefined
}

export function asStringArray(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const out = v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map(s => s.trim())
    return out.length ? out : undefined
  }
  const single = asString(v)
  return single ? [single] : undefined
}

export function group(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {}
}

// Normalize a requested currency to a supported one, falling back to the
// visitor default when it's missing or unrecognized.
export function pickCurrency(
  supportedCurrencies: Currency[],
  defaultCurrency: Currency,
  c?: string
): Currency {
  const upper = (c || '').toUpperCase()
  return (supportedCurrencies as string[]).includes(upper)
    ? (upper as Currency)
    : defaultCurrency
}

export interface ReadOnlyWebMcpArgs {
  plans: Record<string, PlanInfo[]>
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  countries: Country[]
  supportedCurrencies: Currency[]
  defaultCurrency: Currency
}

// Builds the two public-info, read-only tools. Both `/apply` and the homepage
// register these from this single source so the copy and behavior stay in sync.
export function buildReadOnlyWebMcpTools(args: ReadOnlyWebMcpArgs): WebMcpTool[] {
  const { plans, pricing, countries, supportedCurrencies, defaultCurrency } = args
  const pick = (c?: string): Currency => pickCurrency(supportedCurrencies, defaultCurrency, c)

  const planLine = (plan: PlanInfo, currency: Currency): string => {
    const cycle = pricing[plan.key]
    const yearly = cycle?.yearly?.[currency]?.discounted_price
    const monthly = cycle?.monthly?.[currency]?.discounted_price
    const price =
      yearly != null && monthly != null
        ? `${currency} ${yearly}/yr (${currency} ${monthly}/mo)`
        : 'see wavbee.com/pricing'
    return `- ${plan.name} (${plan.royalties} royalties): ${price}. ${plan.subtitle}`
  }

  const listPlansTool: WebMcpTool = {
    name: 'wavbee_list_plans_and_pricing',
    description:
      "List wavbee's music-distribution plans with their royalty share and price. " +
      'Use this to answer questions about how much wavbee costs and which plan fits an artist or label. ' +
      'Prices are available in USD, EUR, INR, and THB.',
    inputSchema: {
      type: 'object',
      properties: {
        currency: {
          type: 'string',
          enum: supportedCurrencies,
          description: 'Currency for prices (USD, EUR, INR, THB). Defaults to the visitor currency.'
        }
      }
    },
    execute: (a) => {
      const currency = pick(asString(a.currency))
      const artist = (plans.artist || []).map(p => planLine(p, currency)).join('\n')
      const label = (plans.label || []).map(p => planLine(p, currency)).join('\n')
      return text(
        `wavbee distribution plans (prices in ${currency}; yearly billing shown, monthly in brackets).\n` +
          'All plans include unlimited releases (no per-release fee), 100+ stores, free UPC/ISRC, ' +
          'Content ID, daily analytics, and revenue splits.\n\n' +
          `Artist plans (for solo artists):\n${artist}\n\n` +
          `Label plans (for labels with multiple artists):\n${label}\n\n` +
          'Pro plans add priority delivery (24-48h) and playlist pitching. Full details: https://wavbee.com/pricing'
      )
    }
  }

  const checkCountryTool: WebMcpTool = {
    name: 'wavbee_check_country_supported',
    description:
      'Check whether wavbee accepts applications from a given country. A small number of countries ' +
      'are restricted; everywhere else can apply. Returns whether the country is supported.',
    inputSchema: {
      type: 'object',
      properties: {
        country: {
          type: 'string',
          description: 'Country name (e.g. "India") or ISO code (e.g. "IN").'
        }
      },
      required: ['country']
    },
    execute: (a) => {
      const q = (asString(a.country) || '').toLowerCase()
      if (!q) return text('Please provide a country name or ISO code.')
      const match = countries.find(
        c => c.name.toLowerCase() === q || c.code.toLowerCase() === q
      )
      if (match) {
        return text(
          `Yes - wavbee accepts applications from ${match.name}. ` +
            'You can apply at https://wavbee.com/apply'
        )
      }
      return text(
        `"${a.country}" is not in wavbee's selectable country list. It may be spelled differently, ` +
          'or it may be one of the few restricted countries that cannot currently apply. ' +
          'See the country selector at https://wavbee.com/apply'
      )
    }
  }

  return [listPlansTool, checkCountryTool]
}

// Is there anything here that could USE a WebMCP tool? Exported so a caller can
// ask BEFORE doing expensive work — fetching tool data into a browser that has
// no agent to call it is pure waste, and today that is almost every browser.
// `registerWebMcpTools` re-checks, so this is an optimisation, not the guard.
export function hasModelContext(): boolean {
  if (typeof navigator === 'undefined') return false
  return Boolean((navigator as NavigatorWithModelContext).modelContext)
}

// Feature-detected registration. Publishes `tools` to the browser's model
// context (preferring `provideContext`, which REPLACES the whole toolset;
// falling back to the incremental `registerTool` API) and returns an effect
// cleanup that withdraws them again. When `navigator.modelContext` is absent
// this is a no-op and returns undefined.
export function registerWebMcpTools(tools: WebMcpTool[]): (() => void) | undefined {
  if (typeof navigator === 'undefined') return
  const ctx = (navigator as NavigatorWithModelContext).modelContext
  if (!ctx) return

  if (ctx.provideContext) {
    ctx.provideContext({ tools })
  } else if (ctx.registerTool) {
    tools.forEach(t => ctx.registerTool?.(t))
  }

  return () => {
    const cleanup = (navigator as NavigatorWithModelContext).modelContext
    if (!cleanup) return
    if (cleanup.unregisterTool) {
      tools.forEach(t => cleanup.unregisterTool?.(t.name))
    } else if (cleanup.provideContext) {
      cleanup.provideContext({ tools: [] })
    }
  }
}
